import type { ReactNode } from "react";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  checkDeviceLocation,
  type DeviceLocationStatus,
} from "@/util/deviceLocationBridge";
import { useMobileBridge } from "@/hooks/useMobileBridge";
import { useAuthStore } from "@/contexts/AuthContext";

interface WebViewBridgeContextType {
  deviceLocationEnabled: DeviceLocationStatus;
  checkDeviceLocation: () => Promise<DeviceLocationStatus>;
  openDeviceLocationSettings: () => void;
  /** true when RN reports the user has denied notification permission */
  rnNotifPermissionDenied: boolean;
  /** Call with false to dismiss the reminder modal */
  setRnNotifPermissionDenied: (val: boolean) => void;
  /** Instructs the React Native wrapper to open the device settings page */
  openAppSettings: () => void;
  /** Sets the iOS app badge count locally via the WebView bridge */
  setAppBadgeCount: (count: number) => void;
}

const WebViewBridgeContext = createContext<
  WebViewBridgeContextType | undefined
>(undefined);

export const WebViewBridgeProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user, isLoading } = useAuthStore();
  const [rnNotifPermissionDenied, setRnNotifPermissionDenied] = useState(false);
  const [deviceLocationEnabled, setDeviceLocationEnabled] =
    useState<DeviceLocationStatus>(null);
  const handleNotificationDenied = useCallback(() => {
    setRnNotifPermissionDenied(true);
  }, []);
  const refreshDeviceLocation = useCallback(async () => {
    const enabled = await checkDeviceLocation();
    setDeviceLocationEnabled(enabled);
    return enabled;
  }, []);

  // ── Native WebView bridge (FCM token sync + notification events) ──────────
  const {
    sendAuthState,
    openAppSettings,
    openDeviceLocationSettings,
    setAppBadgeCount,
  } = useMobileBridge({
    onRnNotificationPermissionDenied: handleNotificationDenied,
    onDeviceLocationStatus: setDeviceLocationEnabled,
  });

  // Send auth state to RN whenever login/logout resolves.
  // Guard on !isLoading so we don't send a false "loggedOut" during hydration.
  useEffect(() => {
    if (isLoading) return;

    if (user) {
      sendAuthState(true, user.email);
    } else {
      sendAuthState(false);
    }
  }, [user, isLoading, sendAuthState]);

  return (
    <WebViewBridgeContext.Provider
      value={{
        deviceLocationEnabled,
        checkDeviceLocation: refreshDeviceLocation,
        openDeviceLocationSettings,
        rnNotifPermissionDenied,
        setRnNotifPermissionDenied,
        openAppSettings,
        setAppBadgeCount,
      }}
    >
      {children}
    </WebViewBridgeContext.Provider>
  );
};

export const useWebViewBridge = () => {
  const context = useContext(WebViewBridgeContext);
  if (context === undefined) {
    throw new Error(
      "useWebViewBridge must be used within a WebViewBridgeProvider"
    );
  }
  return context;
};
